import * as Sentry from '@sentry/react'

import axios, { type AxiosError } from 'axios'
import { ENVIRONMENT, EnvironmentValue } from 'constants/application'

export type LogLevel = 'info' | 'warn' | 'error'

/** What a failed request may report: its method, path, status and error code, never its body or headers. */
const requestErrorOutline = (error: AxiosError) => ({
  method: error.config?.method?.toUpperCase(),
  url: error.config?.url?.split(/[?#]/)[0],
  status: error.response?.status,
  code: error.code,
  message: error.message,
})

export interface LogContext {
  component?: string
  action?: string
  userId?: string
  metadata?: Record<string, any>
}

export interface LoggerOptions {
  /** Whether to log to console (default: true for localhost/development) */
  console?: boolean
  /** Whether to report to Sentry (default: true for warn/error levels) */
  sentry?: boolean
  /** Additional context for the log */
  context?: LogContext
}

class Logger {
  private shouldLogToConsole(): boolean {
    return ENVIRONMENT === EnvironmentValue.LOCALHOST || ENVIRONMENT === EnvironmentValue.DEVELOPMENT
  }

  private shouldReportToSentry(level: LogLevel): boolean {
    return level === 'warn' || level === 'error'
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString()
    const component = context?.component ? `[${context.component}]` : ''
    const action = context?.action ? `[${context.action}]` : ''

    return `${timestamp} ${level.toUpperCase()} ${component}${action} ${message}`
  }

  private logToConsole(level: LogLevel, message: string, context?: LogContext, data?: any): void {
    if (!this.shouldLogToConsole()) return

    const formattedMessage = this.formatMessage(level, message, context)

    switch (level) {
      case 'info':
        console.log(formattedMessage, data || '')
        break
      case 'warn':
        console.warn(formattedMessage, data || '')
        break
      case 'error':
        console.error(formattedMessage, data || '')
        break
    }
  }

  private reportToSentry(level: LogLevel, message: string, context?: LogContext, data?: any): void {
    if (!this.shouldReportToSentry(level)) return

    // 401s are expected (expired/missing token, not-yet-authenticated) and are
    // handled by the auth flow — they are noise in Sentry, so skip reporting them.
    // Mirrors the API.logError guard from ISSUE-2093, closing the logger path (QA-259).
    if (axios.isAxiosError(data) && data.response?.status === 401) return

    Sentry.withScope((scope) => {
      // Set level
      scope.setLevel(level === 'warn' ? 'warning' : 'error')

      // Set context tags
      if (context?.component) {
        scope.setTag('component', context.component)
      }

      if (context?.action) {
        scope.setTag('action', context.action)
      }

      if (context?.userId) {
        scope.setUser({ id: context.userId })
      }

      if (context?.metadata) {
        scope.setContext('metadata', context.metadata)
      }

      // A request error carries its body (contacts, emails, comments) in `config.data`, so only its
      // outline is reported.
      const reported = axios.isAxiosError(data) ? requestErrorOutline(data) : data
      if (reported) {
        scope.setContext('data', reported)
      }

      // Use captureException for errors, captureMessage for warnings
      if (level === 'error' && data instanceof Error) {
        Sentry.captureException(axios.isAxiosError(data) ? new Error(data.message) : data)
      } else {
        Sentry.captureMessage(message)
      }
    })
  }

  /**
   * Log an info message
   */
  info(message: string, data?: any, options?: LoggerOptions): void {
    const { console: logToConsole = true, sentry: reportToSentry = false, context } = options || {}

    if (logToConsole) {
      this.logToConsole('info', message, context, data)
    }

    if (reportToSentry) {
      this.reportToSentry('info', message, context, data)
    }
  }

  /**
   * Log a warning message
   */
  warn(message: string, data?: any, options?: LoggerOptions): void {
    const { console: logToConsole = true, sentry: reportToSentry = true, context } = options || {}

    if (logToConsole) {
      this.logToConsole('warn', message, context, data)
    }

    if (reportToSentry) {
      this.reportToSentry('warn', message, context, data)
    }
  }

  /**
   * Log an error message
   */
  error(message: string, data?: any, options?: LoggerOptions): void {
    const { console: logToConsole = true, sentry: reportToSentry = true, context } = options || {}

    if (logToConsole) {
      this.logToConsole('error', message, context, data)
    }

    if (reportToSentry) {
      this.reportToSentry('error', message, context, data)
    }
  }

  /**
   * Create a logger instance with default context
   */
  withContext(context: LogContext): Logger {
    const logger = new Logger()
    const originalMethods = {
      info: logger.info.bind(logger),
      warn: logger.warn.bind(logger),
      error: logger.error.bind(logger)
    }

    // Override methods to include default context
    logger.info = (message: string, data?: any, options?: LoggerOptions) => {
      const mergedContext = { ...context, ...options?.context }
      originalMethods.info(message, data, { ...options, context: mergedContext })
    }

    logger.warn = (message: string, data?: any, options?: LoggerOptions) => {
      const mergedContext = { ...context, ...options?.context }
      originalMethods.warn(message, data, { ...options, context: mergedContext })
    }

    logger.error = (message: string, data?: any, options?: LoggerOptions) => {
      const mergedContext = { ...context, ...options?.context }
      originalMethods.error(message, data, { ...options, context: mergedContext })
    }

    return logger
  }
}

// Export singleton instance
export const logger = new Logger()

// Export class for creating custom instances
export { Logger }
